import { ILog } from './logging.models';

// creating these alias types to give more context to the string types used in this module.
export type StorageKey = string;
export type StorableLogs = string;

export type LogBatchAndKey = [StorageKey, StorableLogs];

export namespace LogBatcher {
  /*
     rotateBatches
     Oldest batch dropped, new logs inserted as current batch
     Idea is to optimize performance by dropping batches of logs rather
     than cycling one at a time.
  */
  export function rotateBatches(
    batches: LogBatchAndKey[],
    newLogs: ILog[]
  ): LogBatchAndKey[] {
    const _batches: LogBatchAndKey[] = batches || [];
    const _newLogs: string = JSON.stringify(newLogs || []);

    return _batches.map(
      (
        batch: LogBatchAndKey,
        i: number,
        all: LogBatchAndKey[]
      ): LogBatchAndKey => {
        const currentBatchKey: string = batch[0];
        const isLastBatch: boolean = i === all.length - 1;
        return isLastBatch
          ? [currentBatchKey, _newLogs]
          : [currentBatchKey, all[i + 1][1]];
      }
    );
  }

  export function combineBatches(batches: LogBatchAndKey[]): ILog[] {
    const _batches: LogBatchAndKey[] = batches || [];
    return _batches.reduce((accumulator: ILog[], batch: LogBatchAndKey) => {
      return batch[1] ? [...accumulator, ...JSON.parse(batch[1])] : accumulator;
    }, []);
  }

  export function splitLogsIntoBatches(
    logs: ILog[],
    latestBatchCount: number,
    maxBatchSize: number
  ): ILog[][] | [[]] {
    /*
      Takes a collection of logs and splits into a multi-dimensional array
      so that each batch will not exceed the set batch size.
    */
    const canSplitLogs = logs && logs.length && maxBatchSize > 0;
    const firstBatchSize =
      latestBatchCount > 0 && latestBatchCount < maxBatchSize
        ? latestBatchCount
        : 0;

    // split calls itself recursively until the splitting is complete.
    return canSplitLogs
      ? split(0, [], logs, firstBatchSize, maxBatchSize)
      : [[]];
  }

  // split
  /*
    Recursively calls itself splitting 'allLogs' into inner arrays of given
    batchSize until reaches end.
    e.g.

    Input -> 'allLogs' array, 4 logs
    logs = [
      { message: 'foo' },
      { message: 'bar' },
      { message: 'baz' },
      { message: 'foobarbaz' }
    ]

    batchSize  = 2;

    Output -> 'splitLogs' 2D array, with 2 inner arrays, each with 2 logs.
    output = [
      [ { message: 'foo' }, { message:'bar' }],
      [ { message: 'baz' }, { message: 'foobarbaz' }]
    ];
  */
  export function split(
    index: number,
    splitLogs: ILog[][],
    allLogs: ILog[],
    firstBatchSize: number,
    maxBatchSize: number
  ): ILog[][] {
    const startIndex =
      index === 0 ? index : index * maxBatchSize - firstBatchSize;
    const endIndex = (index + 1) * maxBatchSize - firstBatchSize;
    const nextBatch: ILog[] = allLogs.slice(startIndex, endIndex);
    const hasReachedEnd = !nextBatch.length;
    return hasReachedEnd
      ? splitLogs
      : split(
          index + 1,
          [...splitLogs, [...nextBatch]],
          allLogs,
          firstBatchSize,
          maxBatchSize
        );
  }

  export function calcCurrentBatchNumber(
    logCount: number,
    logsPerBatch: number
  ): number {
    return logCount ? Math.floor(logCount / logsPerBatch) : 0;
  }
}
